import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { Description } from '../Description';
import { FieldGroup, Label, useFieldProps } from '../Form';
import { Group } from '../Group';
import { Tooltip, type TooltipProps } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';

export type SwitchProps = {
    /**
     * Optional label which can be clicked to trigger the switch.
     * Increasing the hit area for triggering the ui.
     * @deprecated Use `FieldGroup` and `Label` components instead.
     */
    label?: React.ReactNode;

    /**
     * Optional icon displayed before the label
     * @deprecated Use `FieldGroup` and `Group` components instead.
     */
    labelIcon?: IconDefinition;

    /**
     * Optional description
     * @deprecated Use `FieldGroup` and `Description` components instead.
     */
    labelDescription?: React.ReactNode;

    /**
     * Optional badge displayed after the label
     * @deprecated Use `FieldGroup` and `Group` components instead.
     */
    badge?: React.ReactNode;

    /**
     * The value (true/false) that corresponds to the state of the switch.
     */
    value?: boolean;

    /**
     * Whether the switch is disabled. A disabled switch can not be updated.
     */
    disabled?: boolean;

    /**
     * Whether the switch is in a loading state
     */
    loading?: boolean;

    /**
     * Callback triggered when the value of the switch changes.
     * @param value
     */
    onValueChange?: (value: boolean) => void | Promise<void>;

    /**
     * Optional custom styles that will be passed to the root of the
     * component.
     */
    className?: string;

    /**
     * Optional inline styles applied to the switch track element.
     */
    style?: React.CSSProperties;

    /**
     * Set a unique ID that can be used for testing purposes.
     */
    testID?: string;

    /**
     * Visual style of the switch.
     * Defaults to `secondary`.
     */
    kind?: 'secondary' | 'upgrade';

    /**
     * The size of the switch.
     * @default 'medium'
     */
    size?: 'small' | 'medium';

    /**
     * Optional tooltip shown when hovering or focusing the switch.
     */
    tooltip?: Omit<TooltipProps, 'children'>;

    /**
     * Accessible label applied to the switch control.
     */
    'aria-label'?: string;

    /**
     * Accessible label reference applied to the switch control.
     */
    'aria-labelledby'?: string;
};

/**
 * Switch - an interactive UI for toggling things on and off.
 *
 * A switch is composed of two parts - the track and the thumb.
 *
 * A switch can be either on or off and it has a corresponding value
 * that is either true or false.
 *
 * Can have a loading state by setting the `loading` prop to true.
 */
export function Switch({
    ref: forwardedRef,
    ...props
}: SwitchProps & { ref?: React.ForwardedRef<HTMLInputElement> }) {
    const {
        label,
        labelIcon,
        labelDescription,
        value,
        loading,
        disabled = false,
        onValueChange,
        badge,
        className,
        style,
        testID,
        kind = 'secondary',
        size = 'medium',
        tooltip,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        ...rest
    } = props;

    const controlProps = {
        value,
        onValueChange,
        testID,
        loading,
        disabled,
        kind,
        size,
        style,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
    };

    const switchControl = <SwitchControl {...controlProps} ref={forwardedRef} />;

    const switchElement = (
        <FieldGroup orientation="horizontal" className={className} {...rest}>
            <Group>
                {label ? (
                    <Group>
                        {labelIcon ? renderIcon(labelIcon, { size: 18 }) : null}
                        <Label>{label}</Label>
                        {badge}
                    </Group>
                ) : null}
                {labelDescription && <Description>{labelDescription}</Description>}
            </Group>
            {tooltip ? <Tooltip {...tooltip}>{switchControl}</Tooltip> : switchControl}
        </FieldGroup>
    );

    if (!label && !labelDescription) {
        return tooltip ? (
            <Tooltip {...tooltip}>
                <SwitchControl {...controlProps} className={className} ref={forwardedRef} />
            </Tooltip>
        ) : (
            <SwitchControl {...controlProps} className={className} ref={forwardedRef} />
        );
    }

    return switchElement;
}

const SwitchControl = function SwitchControl({
    ref: forwardedRef,
    ...props
}: Omit<SwitchProps, 'label' | 'labelIcon' | 'labelDescription' | 'badge'> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
}) {
    const {
        value: propValue,
        loading: propLoading = false,
        disabled = false,
        onValueChange,
        className,
        testID,
        kind,
        size = 'medium',
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        onFocus,
        onBlur,
        onKeyDown,
        onKeyUp,
        tabIndex,
        ...rest
    } = props as typeof props & React.HTMLAttributes<HTMLDivElement>;

    const [stateLoading, wrapStateLoading] = useLoadingState();
    const inputRef = useObjectRef(forwardedRef);

    const handleChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const result = onValueChange?.(event.target.checked);
        if (result instanceof Promise) {
            wrapStateLoading(result);
        }
    });

    const isLoading = propLoading || stateLoading;

    const { ref, id, ...fieldProps } = useFieldProps({
        ref: inputRef,
        onChange: handleChange,
        onBlur,
        disabled: disabled || isLoading,
    });

    const value = propValue ?? fieldProps.value;
    const state = isLoading
        ? 'loading'
        : disabled && value
          ? 'checked-disabled'
          : disabled
            ? 'disabled'
            : value
              ? 'checked'
              : undefined;

    return (
        <div
            className={clsx(
                'peer/switch group/switch-track relative flex cursor-pointer rounded-full bg-neutral-ui transition',
                size === 'small' ? 'h-4 w-7' : 'h-5 w-9',
                'data-[state=checked-disabled]:cursor-not-allowed data-[state=checked-disabled]:opacity-disabled data-[state=disabled]:cursor-not-allowed',
                value && (kind === 'upgrade' ? 'bg-upgrade-solid' : 'bg-primary-solid'),
                !value &&
                    'shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--neutral-border-subtle)_40%,transparent)]',
                className
            )}
            data-state={state}
            aria-busy={isLoading}
            aria-live="polite"
            {...rest}
        >
            <input
                type="checkbox"
                role="switch"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-checked={value}
                data-testid={testID}
                ref={ref}
                className="peer/switch-input cursor-inherit absolute inset-0 z-[1] m-0 h-full w-full p-0 opacity-0"
                id={id}
                checked={value}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                tabIndex={tabIndex}
                {...fieldProps}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full outline outline-2 outline-transparent peer-focus-visible/switch-input:focus-ring"
            />
            <div
                aria-hidden
                className={clsx(
                    'pointer-events-none absolute top-0.5 left-0.5 rounded-full bg-white shadow-[0_0.824357px_1.1541px_-0.625px_rgb(0_0_0_/_0.26),0_2.10766px_2.95072px_-1.25px_rgb(0_0_0_/_0.25)] duration-300 ease-snappy-out',
                    size === 'small'
                        ? 'h-3 w-3 peer-checked/switch-input:[translate:0.75rem_0]'
                        : 'h-4 w-4 peer-checked/switch-input:[translate:1rem_0]',
                    'group-data-[state=loading]/switch-track:shadow-none'
                )}
            >
                <div className="absolute inset-0 rounded-full border-[3px] border-transparent group-data-[state=loading]/switch-track:animate-[spin_0.8s_cubic-bezier(0.5,0,0.5,1)_0.1s_infinite] group-data-[state=loading]/switch-track:border-t-[var(--sp-color-text-light)]" />
            </div>
        </div>
    );
};
